import type { VoidFunctionComponent } from 'react';

import { NO_SAFE_VERSION, UNABLE_TO_CALCULATE } from '../../../../shared/vulns/remediation';
import Badge from '../../generic/Badge/Badge';

import styles from './UpgradeBadge.module.less';

type UpgradeBadgeProps = {
  /**
   * The version to upgrade to.
   */
  upgradeVersion: string;
  /**
   * The count of the fixes included in the upgrade.
   */
  fixCount: number;
};

function getUpgradeMetadata(version: string): { label: string; tooltip: string } {
  if (version === NO_SAFE_VERSION) {
    return { label: 'No fix', tooltip: 'Fix unavailable' };
  }
  if (version === UNABLE_TO_CALCULATE) {
    return { label: 'Unknown fix', tooltip: 'The upgrade distance could not be calculated' };
  }
  return { label: `Upgrade to ${version}`, tooltip: 'Upgrade to the safe version' };
}

/**
 * Display upgrade information and fix count in a composite badge.
 */
const UpgradeBadge: VoidFunctionComponent<UpgradeBadgeProps> = ({ upgradeVersion, fixCount }) => {
  if (!upgradeVersion || upgradeVersion === NO_SAFE_VERSION || upgradeVersion === UNABLE_TO_CALCULATE) {
    return null;
  }

  const { label } = getUpgradeMetadata(upgradeVersion);

  return (
    <div className={styles.upgradeBadge}>
      <Badge color="white" shape="box" outline>
        {label}
      </Badge>
      {upgradeVersion !== NO_SAFE_VERSION && upgradeVersion !== UNABLE_TO_CALCULATE && (
        <Badge color="lighter-gray" shape="box">
          Fixes {fixCount}
        </Badge>
      )}
    </div>
  );
};

export default UpgradeBadge;
